import { ColorButton } from "../../../../common/components/button/colorButton/ColorButton";
import { Tag } from "../../../../common/components/common/Tag/Tag";
import { PaymentTypes, UserEventStatus } from "../../../../common/enums";
import { IconType } from "../../../../common/icons/Icon";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import { IPlannerEventUserModel } from "../models/plannerEventUserModel";
import { KeyValueItem } from "./KeyValueItem";
import styles from "./PlannerEventReservationDetailsReservation.module.scss";
import parentStyles from "./PlannerEventReservationDetails.module.scss";
import { usePlannerEventReservationDetailsReservation } from "../hooks/usePlannerEventReservationDetailsReservation";
import { AppDialog } from "../../../../common/components/AppDialog/AppDialog";
import { PlannerEventReservationDetailsPaymentMethodDialog } from "./PlannerEventReservationDetailsPaymentMethodDialog";
import { IStudioSettingsModel } from "../../../../common/models/IStudioSettingsModel";
import { PriceDisplay } from "../../../../common/components/PriceDisplay/PriceDisplay";
import { IPlannerEventModel } from "../models/plannerEventModel";

interface IPlannerEventReservationDetailsReservationProps {
  user: IPlannerEventUserModel;
  isEventPassed: boolean;
  studioSettings: IStudioSettingsModel | undefined;
  event?: IPlannerEventModel;
}

export const PlannerEventReservationDetailsReservation = ({
  user,
  isEventPassed,
  studioSettings,
  event,
}: IPlannerEventReservationDetailsReservationProps) => {
  const {
    isConfirmingReservation,
    isRemovingPaymentMethod,
    isTogglingFormFulfillment,
    statusColor,
    statusLabel,
    paymentTypeLabel,
    visitsUserLabel,
    shouldShowPricePaid,
    addPaymentMethodUserEventId,
    onEventArrivalConfirmationClick,
    onBackToConfirmedReservation,
    onToggleReservationConfirmation,
    onRemovePaymentMethodClick,
    onToggleFormFulfillment,
    handleConfirmReservation,
    closePaymentMethodDialog,
    openPaymentMethodDialog,
  } = usePlannerEventReservationDetailsReservation(user, isEventPassed, event);

  return (
    <>
      <div className={styles.details_content}>
        <KeyValueItem name={"Номер на резервация"} value={user.userEventId} />
        <KeyValueItem
          name={"Създадена на"}
          value={dateUtility.formatDateTime(user.createdOn)}
        />
        <KeyValueItem
          name={"Статус"}
          value={<Tag label={statusLabel} backgroundColor={statusColor} />}
        />
        <KeyValueItem
          name={"Начин на плащане"}
          value={
            <div className={styles.details_content_payment}>
              {!!paymentTypeLabel ? paymentTypeLabel : "N/A"}
              <RenderWhen condition={!paymentTypeLabel && !user.isCancelled}>
                <ColorButton
                  className={styles.details_content_payment_add}
                  icon={IconType.Plus2}
                  backgroundColorHex={"#10b981"}
                  textColorHex={"#fff"}
                  onClick={openPaymentMethodDialog}
                />
              </RenderWhen>
              <RenderWhen
                condition={
                  !!paymentTypeLabel &&
                  user.status !== UserEventStatus.Arrived &&
                  user.status !== UserEventStatus.OnlyPaid
                }
              >
                <ColorButton
                  className={styles.details_content_payment_add}
                  icon={IconType.Cancel}
                  backgroundColorHex={"#ef4444"}
                  textColorHex={"#fff"}
                  loading={isRemovingPaymentMethod}
                  onClick={onRemovePaymentMethodClick}
                />
              </RenderWhen>
            </div>
          }
        />

        <RenderWhen condition={!!user.pricePaid && shouldShowPricePaid}>
          <KeyValueItem
            name={"Сума"}
            value={
              <PriceDisplay
                minPrice={user?.pricePaid ?? 0}
                currencyId={user?.currencyId}
              />
            }
          />
        </RenderWhen>

        <RenderWhen
          condition={
            user.paymentType === PaymentTypes.Plan && !!user?.visitsUsed
          }
        >
          <KeyValueItem
            name={"Поредно посещение"}
            value={
              <span className={styles.details_content_visits}>
                {visitsUserLabel}
              </span>
            }
          />
        </RenderWhen>

        <RenderWhen condition={user.isCancelled && !!user.deletedOn}>
          <KeyValueItem
            name={"Отказана на"}
            value={dateUtility.formatDateTime(user.deletedOn)}
          />
        </RenderWhen>

        <RenderWhen condition={event?.requireFormCompletion === true}>
          <KeyValueItem
            name={"Попълнен формуляр"}
            value={
              <div className={styles.details_content_payment}>
                {user.hasFilledForm ? "Да" : "Не"}
                <RenderWhen condition={!user.isCancelled}>
                  <ColorButton
                    className={styles.details_content_payment_add}
                    icon={user.hasFilledForm ? IconType.Cancel : IconType.Valid}
                    backgroundColorHex={
                      user.hasFilledForm ? "#ef4444" : "#10b981"
                    }
                    textColorHex={"#fff"}
                    loading={isTogglingFormFulfillment}
                    onClick={onToggleFormFulfillment}
                    tooltip={
                      user.hasFilledForm
                        ? "Маркирай като непопълнен"
                        : "Маркирай като попълнен"
                    }
                  />
                </RenderWhen>
              </div>
            }
          />
        </RenderWhen>
      </div>

      {/* isCancelled, like the status tag and the rest of the planner: a reservation
          revived before the backend fix could still carry an old deletedOn. */}
      <RenderWhen condition={!user.isCancelled}>
        <RenderWhen
          condition={
            user?.status === UserEventStatus.Pending ||
            user?.status === UserEventStatus.WaitingList
          }
        >
          <ColorButton
            className={parentStyles.common_small_button}
            label={"Потвърди"}
            backgroundColorHex={"rgba(112, 184, 239, 1)"}
            textColorHex={"#fff"}
            onClick={handleConfirmReservation}
            loading={isConfirmingReservation}
          />
        </RenderWhen>
        <RenderWhen condition={user?.status === UserEventStatus.Confirmed}>
          <div className={parentStyles.common_buttons_container}>
            <RenderWhen condition={!isEventPassed}>
              <ColorButton
                label={"Премахни потвърждението"}
                backgroundColorHex={"rgba(234, 137, 241, 1)"}
                textColorHex={"#fff"}
                onClick={() => onToggleReservationConfirmation()}
                loading={isConfirmingReservation}
              />
            </RenderWhen>

            <ColorButton
              label={"Маркирай като взета"}
              backgroundColorHex={"#3b82f6"}
              textColorHex={"#fff"}
              onClick={onEventArrivalConfirmationClick}
              disabled={isConfirmingReservation}
            />
          </div>
        </RenderWhen>

        <RenderWhen
          condition={
            user?.status === UserEventStatus.OnlyPaid ||
            user?.status === UserEventStatus.Arrived
          }
        >
          <ColorButton
            label={"Върни към статус 'Потвърдена'"}
            backgroundColorHex={"#3b82f6"}
            textColorHex={"#fff"}
            onClick={onBackToConfirmedReservation}
            disabled={isConfirmingReservation}
          />
        </RenderWhen>

        <AppDialog
          header={"Добавяне на начин на плащане"}
          contentClassName={styles.dialog}
          visible={addPaymentMethodUserEventId > 0}
          onHide={closePaymentMethodDialog}
        >
          <PlannerEventReservationDetailsPaymentMethodDialog
            userEventId={user.userEventId}
            onSuccess={closePaymentMethodDialog}
            showFormCompletionCheckbox={event?.requireFormCompletion}
          />
        </AppDialog>
      </RenderWhen>
    </>
  );
};
